"use client";

import { useCallback, useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import CdnImage from "@/components/CdnImage";
import { useModalA11y } from "@/hooks/useModalA11y";

export type LightboxSlide = {
  /** Resized at the edge via CdnImage. Ignored when `node` is set. */
  src?: string;
  alt: string;
  caption?: string;
  /** Custom slide content (e.g. the PDP room composite) rendered instead of a plain image. */
  node?: ReactNode;
};

export type LightboxLabels = { close: string; previous: string; next: string };

// English fallbacks so shared consumers (ReviewMedia in the admin) can render this
// outside the localized [locale] tree; marketing callers pass translated labels.
const DEFAULT_LABELS: LightboxLabels = { close: "Close", previous: "Previous image", next: "Next image" };

/**
 * Full-screen viewer behind the marketing/store images: any picture shown at
 * thumbnail or column width opens full size with one tap. Backdrop click and
 * Escape close it, arrow keys / edge buttons step through multi-image sets,
 * and useModalA11y supplies the focus trap, scroll lock and focus restore.
 */
export function ImageLightbox({
  slides,
  index,
  onIndexChange,
  onClose,
  labels = DEFAULT_LABELS,
}: {
  slides: LightboxSlide[];
  index: number;
  onIndexChange: (next: number) => void;
  onClose: () => void;
  labels?: LightboxLabels;
}) {
  const panelRef = useModalA11y<HTMLDivElement>(true, onClose);
  const count = slides.length;

  const step = useCallback(
    (delta: number) => onIndexChange((index + delta + count) % count),
    [index, count, onIndexChange],
  );

  useEffect(() => {
    if (count <= 1) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowRight") step(1);
      else if (event.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [count, step]);

  const slide = slides[index];
  if (!slide) return null;

  // Portaled straight to <body>: rendered inline, a sticky/positioned ancestor (e.g. the
  // PDP gallery's `lg:sticky` column) can trap this in its own stacking context, where no
  // z-index escapes it — the site's fixed/absolute chrome would paint over a "full-screen"
  // backdrop that never actually reaches the viewport's top stacking layer.
  return createPortal(
    <div
      // z-[70], above the cart drawer's z-[60]: the drawer can open this viewer on a line's
      // artwork, and a full-screen viewer that paints UNDER the panel that opened it shows the
      // shopper a dimmed page and none of the picture they asked to see.
      className="fixed inset-0 z-[70] bg-ink/90"
      data-testid="image-lightbox"
      onClick={onClose}
      role="presentation"
    >
      <div
        aria-label={slide.alt}
        aria-modal="true"
        className="relative flex h-full w-full flex-col outline-none"
        ref={panelRef}
        role="dialog"
        tabIndex={-1}
      >
        <div className="flex items-center justify-between gap-4 px-4 pb-2 pt-[max(0.75rem,env(safe-area-inset-top))]">
          <span className="min-w-0 truncate font-mono text-[10px] uppercase tracking-[0.11em] text-paper/75">
            {slide.caption ?? slide.alt}
            {count > 1 ? (
              <span className="text-paper/50">
                {" "}
                · {index + 1}/{count}
              </span>
            ) : null}
          </span>
          <button
            className="-m-2.5 shrink-0 p-2.5 font-mono text-[10px] uppercase tracking-[0.11em] text-paper/75 underline decoration-dotted underline-offset-4 hover:text-paper"
            onClick={onClose}
            type="button"
          >
            {labels.close} ✕
          </button>
        </div>

        {/* Clicks on the image itself must not fall through to the backdrop's close handler. */}
        <div
          className="flex min-h-0 flex-1 items-center justify-center px-4 pb-[max(1rem,env(safe-area-inset-bottom))] md:px-16"
          onClick={(event) => event.stopPropagation()}
          role="presentation"
        >
          {slide.node ? (
            <div className="relative aspect-[4/3] max-h-full w-full max-w-[calc((100vh-8rem)*4/3)] overflow-hidden">
              {slide.node}
            </div>
          ) : (
            <div className="relative h-full w-full">
              <CdnImage alt={slide.alt} className="object-contain" fill sizes="100vw" src={slide.src ?? ""} />
            </div>
          )}
        </div>

        {count > 1 ? (
          <>
            <button
              aria-label={labels.previous}
              className="absolute left-2 top-1/2 -translate-y-1/2 border border-paper/25 bg-ink/60 p-3 text-paper/80 transition-colors hover:border-paper/60 hover:text-paper md:left-4"
              onClick={(event) => {
                event.stopPropagation();
                step(-1);
              }}
              type="button"
            >
              <ChevronLeft aria-hidden className="h-5 w-5" strokeWidth={1.5} />
            </button>
            <button
              aria-label={labels.next}
              className="absolute right-2 top-1/2 -translate-y-1/2 border border-paper/25 bg-ink/60 p-3 text-paper/80 transition-colors hover:border-paper/60 hover:text-paper md:right-4"
              onClick={(event) => {
                event.stopPropagation();
                step(1);
              }}
              type="button"
            >
              <ChevronRight aria-hidden className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
